<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import {
  MateCardFactoryGenerateType,
  MateCardPromptType,
} from "@/enums/chatEnums";
import GenerateBtnGroup from "@/components/mobile/common/generate/GenerateBtnGroup.vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";
import { GenerateMateContext } from "@/types/chat";
import { useTextareaAutosize } from "@vueuse/core";
import { Popup } from "vant";
import { useScreenBreakpoints } from "@/composable/client";

const props = withDefaults(
  defineProps<{
    label: string;
    modelValue?: string;
    placeholder?: string;
    promptType: MateCardPromptType;
    mateContext?: GenerateMateContext;
    enableGenerate?: boolean;
    from?: number | null;
    to?: number | null;
    chatSceneName?: string;
    chatSceneDescription?: string;
    name?: string;
    componentType?: "reaction" | "greeting" | "targetReaction";
  }>(),
  {
    placeholder: "",
    modelValue: "",
    mateContext: () => ({}),
    enableGenerate: true,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", val: string): void;
  (e: "blur"): void;
}>();

// States
const { t } = useI18n();
const generating = ref(false);
const inspireTxt = ref<string>("");
const generateType = ref<MateCardFactoryGenerateType>(
  MateCardFactoryGenerateType.Overwrite
);
const { textarea, input } = useTextareaAutosize();
const { laptop } = useScreenBreakpoints();
const contentHtml = computed(() => {
  if (!props.modelValue) {
    return `<p class="text-base-content/50">${t(
      "Message.textGeneration.emptyText"
    )}<p>`;
  }
  return marked.parse(props.modelValue);
});
const showPreview = ref(false);
// props.modelValue与input的双向绑定
watch(
  () => props.modelValue,
  (val) => (input.value = val),
  { immediate: true }
);
watch(
  () => input.value,
  (val) => emit("update:modelValue", val)
);
</script>
<template>
  <div class="form-control w-full flex flex-col gap-1">
    <div v-if="enableGenerate" class="w-full flex flex-col lg:flex-row gap-1">
      <input
        v-if="generateType === MateCardFactoryGenerateType.Overwrite"
        v-model="inspireTxt"
        :disabled="generating"
        :placeholder="
          $t('Message.textGeneration.inspirePlaceholder', { label: label })
        "
        class="input-sm input input-bordered text-xs flex-grow rounded bg-base-200"
        type="text"
      />
      <GenerateBtnGroup
        v-model:generateType="generateType"
        v-model:generating="generating"
        :inspire-txt="inspireTxt"
        :is-universal="false"
        :mate-context="mateContext"
        :model-value="props.modelValue"
        :prompt-type="props.promptType"
        :from="from"
        :to="to"
        :chat-scene-name="chatSceneName"
        :chat-scene-description="chatSceneDescription"
        :name="name"
        :component-type="componentType"
        @update:modelValue="emit('update:modelValue', $event)"
      />
    </div>
    <div class="join join-vertical rounded">
      <div class="relative flex join-item">
        <textarea
          ref="textarea"
          v-model="input"
          :disabled="generating"
          :placeholder="placeholder"
          class="input input-bordered w-full textarea min-h-[8rem] text-xs bg-base-200 rounded-bl-none rounded-br-none overflow-auto max-h-[16rem]"
          @blur="emit('blur')"
        />
        <div
          class="absolute bottom-0 right-0 p-0.5 flex rounded-tl border-l border-t"
        >
          <i class="iconify mingcute--markdown-line" />
        </div>
      </div>
      <button
        :disabled="modelValue.trim().length === 0"
        class="btn btn-neutral btn-xs join-item"
        @click="showPreview = true"
      >
        <i class="iconify mingcute--eye-2-line" />
        {{ $t("Buttons.preview") }}
      </button>
    </div>
    <Popup
      v-model:show="showPreview"
      :class="{
        'w-full': !laptop,
        'w-[40rem]': laptop,
      }"
      :position="laptop ? 'center' : 'bottom'"
      class="min-h-[50%] grid grid-rows-[auto_1fr] max-h-[80%] overflow-hidden"
      closeable
      round
    >
      <div
        class="p-4 flex items-center justify-center border-b border-base-content/10 w-full"
      >
        {{ $t("Buttons.preview") }}
      </div>
      <div class="w-full h-full p-4 overflow-y-auto overflow-x-hidden">
        <div class="chat-container" v-html="contentHtml"></div>
      </div>
    </Popup>
  </div>
</template>
